import { useCallback, useEffect, useRef, useState } from 'react';

// RSP-5383 — the relational click-through banner auto-dismisses after 5s.
const AUTO_DISMISS_MS = 5000;

/**
 * Owns the relational-clickthrough banner's visibility + the 5s auto-dismiss
 * timer. `trigger()` shows the banner and (re)starts the timer, so a repeat
 * click before dismissal resets the countdown (AC2/AC3). State is owned per
 * `UniversalChart`, giving every widget an independent banner (AC7). The timer
 * is cleared on unmount.
 *
 * Lives in its own file (not alongside `RelationalClickthroughBanner`) so that
 * component file only exports a component — keeping React Fast Refresh happy
 * (`react-refresh/only-export-components`).
 */
export const useAutoDismissBanner = (timeoutMs: number = AUTO_DISMISS_MS) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const trigger = useCallback(() => {
    clear();
    setVisible(true);
    timerRef.current = setTimeout(() => {
      setVisible(false);
      timerRef.current = null;
    }, timeoutMs);
  }, [clear, timeoutMs]);

  useEffect(() => clear, [clear]);

  return { visible, trigger };
};
